import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { ApiError } from './errors';

const DEFAULT_OPTIONS = {
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include',
};

export const chatService = {
  async sendMessage(message, chatId = null, signal) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.chat), {
        ...DEFAULT_OPTIONS,
        method: 'POST',
        body: JSON.stringify({ message, chat_id: chatId || undefined }),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  async getSessionInfo() {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.session), {
        ...DEFAULT_OPTIONS,
        method: 'GET',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return {
            session_id: null,
            user_id: null,
            role: 'guest',
          };
        }
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching session info:', error);
      throw error;
    }
  },

  async createSession() {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.session), {
        ...DEFAULT_OPTIONS,
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  },

  async createChat(signal) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.chats), {
        ...DEFAULT_OPTIONS,
        method: 'POST',
        body: JSON.stringify({}),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('Error creating chat:', error);
      throw error;
    }
  },

  async getChat(chatId, signal) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.chatInfo(chatId)), {
        ...DEFAULT_OPTIONS,
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching chat:', error);
      throw error;
    }
  },

  async getChatHistory(chatId, signal) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.chatHistory(chatId)), {
        ...DEFAULT_OPTIONS,
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('Error fetching chat history:', error);
      throw error;
    }
  },

  async listChats({ limit = 20, cursor = null, signal } = {}) {
    try {
      const params = new URLSearchParams({ limit: String(limit) });
      if (cursor) {
        params.set('cursor', String(cursor));
      }
      const response = await fetch(getApiUrl(`${API_ENDPOINTS.chats}?${params.toString()}`), {
        ...DEFAULT_OPTIONS,
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error;
      }
      console.error('Error listing chats:', error);
      throw error;
    }
  },

  async appleSignIn(authorizationCode, redirectUri) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.auth.apple), {
        ...DEFAULT_OPTIONS,
        method: 'POST',
        body: JSON.stringify({
          authorization_code: authorizationCode,
          redirect_uri: redirectUri,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText =
          errorData.detail ||
          errorData.message ||
          errorData.code ||
          `HTTP error! status: ${response.status}`;
        throw new ApiError(messageText, {
          status: response.status,
          code: errorData.code,
          fieldErrors: errorData.field_errors,
          raw: errorData,
        });
      }

      return await response.json();
    } catch (error) {
      console.error('Error with Apple Sign-In:', error);
      throw error;
    }
  },

  async appleSignInCallback(code, state) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.auth.appleCallback), {
        ...DEFAULT_OPTIONS,
        method: 'POST',
        body: JSON.stringify({
          code: code,
          state: state,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText = errorData.detail || errorData.message || errorData.code || `HTTP error! status: ${response.status}`;
        throw new Error(messageText);
      }

      return await response.json();
    } catch (error) {
      console.error('Error with Apple Sign-In callback:', error);
      throw error;
    }
  },

  async handleSystemMessage(action, sessionId, userData = null) {
    try {
      const response = await fetch(getApiUrl(API_ENDPOINTS.auth.systemMessage), {
        ...DEFAULT_OPTIONS,
        method: 'POST',
        body: JSON.stringify({
          action: action,
          session_id: sessionId,
          user_data: userData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const messageText = errorData.detail || errorData.message || errorData.code || `HTTP error! status: ${response.status}`;
        throw new Error(messageText);
      }

      return await response.json();
    } catch (error) {
      console.error('Error handling system message:', error);
      throw error;
    }
  },
};
